import { Col, Form, Row } from "react-bootstrap";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  BackButton,
  Box,
  ButtonStyled,
  SpanStyled,
} from "../../../components/styled/Elements";
import Breadcrumbs from "../../../components/admin/Breadcrumbs/Breadcrumbs";
import ValidatedInput from "../../../components/common/inputFields/ValidatedInput";
import { LazyLoadDropdown } from "../../../components/common/inputFields/LazyLoadDropdown";
import {
  LeftArrowBlack,
  RightArrowWhite,
  RightArrowWithoutBar,
} from "../../../components/styled/AllImages";
import DynamicTableList from "../../../components/common/tables/DynamicTableList";
import SelectProductModal from "./components/modals/SelectProductModal";
import { useEffect, useState } from "react";
import SelectCategoryModal from "./components/modals/SelectCategoryModal";
import { CreateQuotationListColumns } from "../../../helpers/adminConstants/TableConstants";
import { formatAmountForDisplay } from "../../../utils";
import QuotationsApi from "../../../services/api/quotationsApi";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import ClientsApi from "../../../services/api/clientsApi";
import { useUser } from "../../../context/userContext";

const AddQuotation = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      quotation_name: "",
      client_id: null,
      discount_type: null,
      discount: "",
      taxable_amount: "",
      tax: "",
      amount: "",
      notes: "",
      products: [],
    },
  });
  const currentRow = getValues("current_input_row");
  const clientTo = watch("client_id");
  const { user } = useUser();
  const vatValue = user?.organization?.vat_percentage;
  const vatRate = vatValue ? parseInt(vatValue) : 0;
  const { productsList, categoriesList } = useSelector((state) => state.helper);

  const [clientOptions, setclientOptions] = useState();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "products",
    // defaultValues: { products: [] },
  });

  const handleAddItemRow = () => {
    const newRow = getValues("current_input_row");

    if (
      // !newRow?.category ||
      !newRow?.product ||
      !newRow?.quantity ||
      !newRow?.unit_price
    )
      return;
    const amount = parseFloat(newRow.quantity) * parseFloat(newRow.unit_price);
    append({
      ...newRow,
      discount: newRow?.discount || 0,
      // total: amount,
      // net_total: amount,
      // subtotal: amount,
      // vat: (amount * vatRate) / 100,
    });
    setValue("current_input_row", {});
  };

  const [loading, setLoading] = useState(false);
  const [showSelectProductModal, setShowSelectProductModal] = useState(false);
  const [showSelectCategoryModal, setShowSelectCategoryModal] = useState(false);
  const [inputRow, setInputRow] = useState({
    // category: "",
    product: "",
    quantity: "",
    unit_price: "",
    subtotal: "",
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [currentProduct, setCurrentProduct] = useState(null);
  const selectedClient =
    clientOptions &&
    Array.isArray(clientOptions) &&
    clientOptions.length > 0 &&
    clientOptions?.find((item) => Number(item?.value) === Number(clientTo));

  const watchedProducts = watch("products"); // subscribe to products array

  // Derive overall summary
  const calculateSummary = () => {
    let grossTotal = 0; // total before discount
    let totalDiscount = 0; // all discounts
    let netTotal = 0; // total after discounts
    let totalVat = 0; // sum of VAT
    let grandTotal = 0; // final payable amount

    watchedProducts?.forEach((p) => {
      const total = parseFloat(p.total) || 0;
      const discount = parseFloat(p.discount) || 0;
      const net = parseFloat(p.net_total) || 0;
      const vat = parseFloat(p.vat) || 0;
      const subtotal = parseFloat(p.subtotal) || 0;

      grossTotal += total;
      totalDiscount += discount;
      netTotal += net;
      totalVat += vat;
      grandTotal += subtotal;
    });
    setValue(`sub_total`, grossTotal.toFixed(2));
    setValue(`discount`, totalDiscount.toFixed(2));
    setValue(`amount_after_discount`, netTotal.toFixed(2));
    setValue(`tax_value`, totalVat.toFixed(2));
    setValue(`total_amount`, grandTotal.toFixed(2));

    return { grossTotal, totalDiscount, netTotal, totalVat, grandTotal };
  };

  const summary = calculateSummary();

  // const recalculateRow = (index, rowData) => {
  //   const quantity = parseFloat(rowData.quantity) || 0;
  //   const unitPrice = parseFloat(rowData.unit_price) || 0;
  //   const discount = parseFloat(rowData.discount) || 0;

  //   // Base total before discount
  //   const total = quantity * unitPrice;

  //   // Apply discount
  //   const netTotal = Math.max(total - discount, 0);

  //   // VAT amount (not editable, comes from backend rate)
  //   const vat = (netTotal * vatRate) / 100;

  //   // Final subtotal (net + VAT)
  //   const subtotal = netTotal + vat;
  //   // Update form state
  //   setValue(`products.${index}.total`, total.toFixed(2));
  //   setValue(`products.${index}.net_total`, netTotal.toFixed(2));
  //   setValue(`products.${index}.vat`, vat.toFixed(2));
  //   setValue(`products.${index}.subtotal`, subtotal.toFixed(2));
  // };
  const recalculateRow = (index, rowData) => {
    const qty = parseFloat(rowData.quantity) || 0;
    const unitPrice = parseFloat(rowData.unit_price) || 0;
    const discount = parseFloat(rowData.discount) || 0;

    let total = qty * unitPrice;
    if (discount > total) {
      // prevent discount exceeding total
      toast.error(t("discount_cant_be_greater_then_total"));
      rowData.discount = 0;
    }

    let netTotal = total - (parseFloat(rowData.discount) || 0);
    let vat = (netTotal * vatRate) / 100;
    let subtotal = netTotal + vat;

    const updatedRow = {
      ...rowData,
      discount: rowData?.discount || "",
      total: total.toFixed(2),
      net_total: netTotal.toFixed(2),
      vat: vat.toFixed(2),
      subtotal: subtotal.toFixed(2),
    };

    if (index === "current_input_row") {
      setValue("current_input_row", { ...updatedRow });
    } else {
      setValue(`products.${index}`, updatedRow);
    }
  };

  const getClientsList = async () => {
    try {
      const { data, code } = await ClientsApi.clientsList(1, "", null, false);
      if (code === 200 && data) {
        setclientOptions(
          data?.map((item, _) => ({
            ...item,
            value: item?.id,
            label: language === "en" ? item?.name_en : item?.name_ar,
          }))
        );
      }
    } catch (error) {}
  };

  useEffect(() => {
    getClientsList();
  }, [language]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const dataObj = {
        ...data,
        discount_type: 1,
        action: "create",
      };
      const { code, message } = await QuotationsApi.createQuotation(dataObj);
      if (code === 200) {
        toast.success(message);
        navigate("/quotations");
      }
      // if (code === 422 && message) {
      //   toast.error(message);
      // }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3 gap-md-5">
          <BackButton
            label={t("create_quotation")}
            onClick={() => navigate(`/quotations`)}
          />
          <Breadcrumbs />
        </Box>
      </Box>
      <Box className="content-container">
        <FormWrapper>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="name"
                  type="input"
                  fieldType="text"
                  placeholder="quotation_for_office_supplies"
                  label="quotation_name"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: true,
                  }}
                />
              </Col>
              <Col md={6}>
                <LazyLoadDropdown
                  label="to"
                  weight="600"
                  name="client_id"
                  control={control}
                  placeholder={t("select_client")}
                  options={clientOptions}
                  value={clientTo}
                  register={register}
                  errors={errors}
                  validationRules={{ required: true }}
                />
              </Col>
              {/* {selectedClient && (
                <>
                  {selectedClient?.tax_registration_number && (
                    <Col md={6}>
                      <ValidatedInput
                        weight="600"
                        column={true}
                        start={true}
                        name="tax_registration_number"
                        type="input"
                        fieldType="text"
                        disabled={true}
                        placeholder="tax_registration_number"
                        label="tax_registration_number"
                        className="combined px-0"
                        value={selectedClient?.tax_registration_number}
                      />
                    </Col>
                  )}
                  {selectedClient?.phone && (
                    <Col md={6}>
                      <ValidatedInput
                        weight="600"
                        column={true}
                        start={true}
                        name="phone"
                        type="input"
                        fieldType="text"
                        disabled={true}
                        placeholder="phone"
                        label="phone"
                        className="combined px-0"
                        value={selectedClient.phone}
                      />
                    </Col>
                  )}
                </>
              )} */}
            </Row>
            <Row className="mt-3 mb-3">
              <DynamicTableList
                watch={watch}
                remove={remove}
                setValue={setValue}
                getValues={getValues}
                cols={CreateQuotationListColumns}
                register={register}
                fields={fields}
                inputRow={inputRow}
                setInputRow={setInputRow}
                handleAddItemRow={handleAddItemRow}
                setShowSelectProductModal={setShowSelectProductModal}
                selectedProduct={selectedProduct}
                setShowSelectCategoryModal={setShowSelectCategoryModal}
                selectedCategory={selectedCategory}
                setEditingRowIndex={setEditingRowIndex}
                recalculateRow={recalculateRow}
                setCurrentProduct={setCurrentProduct}
                currentRow={currentRow}
              />
            </Row>

            {/* <Row>
              <Col md={6}>
                <LazyLoadDropdown
                  label="discount_type"
                  weight="600"
                  name="discount_type"
                  control={control}
                  placeholder={t("select_discount_type")}
                  options={discountOptions}
                  value={watch("discount_type")}
                  register={register}
                  errors={errors}
                  validationRules={{ required: true }}
                />
              </Col>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="discount"
                  type="input"
                  fieldType="number"
                  placeholder="example_100"
                  label="discount"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: true,
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="taxable_amount"
                  type="input"
                  fieldType="number"
                  placeholder="example_100"
                  label="taxable_amount"
                  className="combined px-0"
                  // staric="staric"
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: true,
                  }}
                />
              </Col>
              <Col md={6}>
                <ValidatedInput
                  weight="600"
                  column={true}
                  start={true}
                  name="tax_percentage"
                  type="input"
                  fieldType="number"
                  placeholder="ten_percent"
                  label="tax"
                  className="combined px-0"
                  // staric="staric"
                  min={0}
                  max={100}
                  register={register}
                  errors={errors}
                  validationRules={{
                    required: true,
                  }}
                />
              </Col>
            </Row>

            <Box className="mb-4">
              <Controller
                name="amount"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value, ref },
                  fieldState: { error },
                }) => (
                  <ValidatedInput
                    weight="600"
                    column={true}
                    start={true}
                    name="amount"
                    type="input"
                    fieldType="text"
                    placeholder="example_10000"
                    label="amount"
                    className="combined px-0"
                    // staric="staric"
                    register={register}
                    errors={errors}
                    validationRules={{
                      required: true,
                    }}
                    value={value ? formatAmountForDisplay(value) : ""}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      onChange(inputValue)
                      const raw = inputValue.replace(/,/g, "");
                      if (
                        raw === "" ||
                        raw === "." ||
                        /^\d*\.?\d{0,2}$/.test(raw)
                      ) {
                        onChange(raw);
                      }
                    }}
                  />
                )}
              />
            </Box> */}

            <Box className="mb-4">
              <ValidatedInput
                weight="600"
                name="notes"
                column={true}
                start={true}
                type="textarea"
                fieldType="text"
                placeholder="write_notes"
                label="notes"
                className="combined px-0"
                // staric="staric"
                register={register}
                errors={errors}
                validationRules={{
                  required: false,
                }}
              />
            </Box>
            <Summary>
              <Box>
                <p>
                  {t("subtotal")}:{" "}
                  <span>
                    {user?.organization?.currency?.stringCode || t("sar")}{" "}
                    {summary?.grossTotal.toFixed(2)}
                  </span>
                </p>
                <p>
                  {t("discount")}:{" "}
                  <span>
                    {user?.organization?.currency?.stringCode || t("sar")}{" "}
                    {summary?.totalDiscount.toFixed(2)}
                  </span>
                </p>
                <p>
                  {t("total_after_discount")}:{" "}
                  <span>
                    {user?.organization?.currency?.stringCode || t("sar")}{" "}
                    {summary?.netTotal.toFixed(2)}
                  </span>
                </p>
                <p>
                  {t("tax")}:{" "}
                  <span>
                    {user?.organization?.currency?.stringCode || t("sar")}{" "}
                    {summary?.totalVat.toFixed(2)}
                  </span>
                </p>
                <p>
                  {t("total_amount")}:{" "}
                  <span>
                    {user?.organization?.currency?.stringCode || t("sar")}{" "}
                    {summary?.grandTotal?.toFixed(2)}
                  </span>
                </p>
              </Box>
            </Summary>

            <FooterButtons>
              <ButtonStyled
                border="var(--borderExtraLight)"
                iconBefore={LeftArrowBlack}
                // icon={RightArrowWithoutBar}
                bg="transparent"
                label={t("Cancel")}
                color="var(--text)"
                type="button"
              />
              <ButtonStyled
                icon={RightArrowWhite}
                type="submit"
                label={t("create")}
                isLoading={loading}
                disabled={loading}
                bg="var(--themeColor)"
              />
            </FooterButtons>
          </Form>
          <SelectProductModal
            optionsList={
              editingRowIndex === null
                ? productsList.filter(
                    (p) =>
                      !watch("current_input_row.category_id") ||
                      Number(p.product_category_id) ===
                        Number(watch("current_input_row.category_id"))
                  )
                : productsList.filter(
                    (p) =>
                      !watch(`products.${editingRowIndex}.category_id`) ||
                      Number(p.product_category_id) ===
                        Number(watch(`products.${editingRowIndex}.category_id`))
                  )
            }
            selectedItem={currentProduct}
            title={t("select_product")}
            show={showSelectProductModal}
            onHide={() => setShowSelectProductModal(false)}
            onSelect={(selectedProduct) => {
              const unitPrice = parseFloat(selectedProduct?.price);
              setCurrentProduct(null);
              if (editingRowIndex === null) {
                const input = watch("current_input_row") || {};
                const dataObj = {
                  ...input,
                  product: selectedProduct.name,
                  product_id: selectedProduct?.id,
                  unit_price: unitPrice,
                  unit: selectedProduct?.unit_of_measurement,
                  category_id: selectedProduct?.product_category_id,
                  subtotal:
                    input.quantity && unitPrice
                      ? parseFloat(input.quantity) * unitPrice
                      : "",
                };
                recalculateRow("current_input_row", dataObj);
                // setValue("current_input_row", {
                //   ...input,
                //   product: selectedProduct.name,
                //   product_id: selectedProduct?.id,
                //   unit_price: unitPrice,
                //   unit: selectedProduct?.unit_of_measurement,
                //   category_id: selectedProduct?.product_category_id,
                //   subtotal:
                //     input.quantity && unitPrice
                //       ? parseFloat(input.quantity) * unitPrice
                //       : "",
                // });
              } else {
                const quantity = parseFloat(
                  watch(`products.${editingRowIndex}.quantity`) || 0
                );
                setValue(
                  `products.${editingRowIndex}.product`,
                  selectedProduct.name
                );
                setValue(
                  `products.${editingRowIndex}.category_id`,
                  selectedProduct?.product_category_id
                );
                setValue(`products.${editingRowIndex}.unit_price`, unitPrice);
                setValue(
                  `products.${editingRowIndex}.unit`,
                  selectedProduct?.unit_of_measurement
                );
                setValue(
                  `products.${editingRowIndex}.subtotal`,
                  (unitPrice * quantity).toFixed(2)
                );
              }

              setShowSelectProductModal(false);
              setEditingRowIndex(null);
            }}
          />
          <SelectCategoryModal
            optionsList={categoriesList}
            title={t("select_category")}
            show={showSelectCategoryModal}
            currentCategoryId={
              editingRowIndex === null
                ? watch("current_input_row.category_id")
                : watch(`products.${editingRowIndex}.category_id`)
            }
            onHide={() => setShowSelectCategoryModal(false)}
            onSelect={(selectedCategory) => {
              if (editingRowIndex === null) {
                console.log(editingRowIndex, "here in category");

                const input = watch("current_input_row") || {};
                setValue("current_input_row", {
                  ...input,
                  category_id: selectedCategory?.id,
                  category: selectedCategory?.name,
                });
              } else {
                setValue(
                  `products.${editingRowIndex}.category_id`,
                  selectedCategory?.id
                );
                setValue(
                  `products.${editingRowIndex}.category`,
                  selectedCategory?.name
                );
              }

              setShowSelectCategoryModal(false);
              setEditingRowIndex(null);
            }}
          />
        </FormWrapper>
      </Box>
    </>
  );
};

export default AddQuotation;

const FormWrapper = styled.div`
  padding: 30px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const FooterButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
`;

const Summary = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: end;
  font-size: 16px;

  p {
    margin: 0.25rem 0;
    color: var(--text);
    font-weight: 700;
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  span {
    color: var(--text);
    font-weight: 500;
  }
`;
